import React, { useId } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Badge, Banner, Button, Tag, relativeTime } from '../kit';
import ThreadLink from './ThreadLink';
import {
  actionsForStatus, actorText, authorText, evidenceParts, exactDate, footerHelp, historyEntries,
  historyText, kindLabel, plural, scopeLabel, shortDate, statusMeta,
} from './skillsLogic';

function Facet({ label, children }) {
  return (
    <div className="sk-section">
      <span className="ak-section-label">{label}</span>
      {children}
    </div>
  );
}

function Tags({ items, mono }) {
  if (!items?.length) return <span className="sk-none">None</span>;
  return (
    <div className={`ak-tags${mono ? ' sk-mono-tags' : ''}`}>
      {items.map((t) => <Tag key={t}>{t}</Tag>)}
    </div>
  );
}

/** Read-only detail of one skill: header, playbook, facets, evidence, history, reason, actions. */
export default function SkillDetail({
  skill, reflection, detailLoading, viewerId, lookups,
  note, onNote, busyAction, actionError, onAction,
}) {
  const noteId = useId();
  const status = statusMeta(skill.status);
  const prov = skill.provenance || {};
  const t = skill.triggers || {};
  const stats = skill.stats || {};
  const evidence = evidenceParts(skill, reflection);
  const history = historyEntries(skill);
  const actions = actionsForStatus(skill.status);
  const statusActions = actions.some((a) => a.to);
  const busy = Boolean(busyAction);
  const lastUsed = stats.lastInjectedAt || stats.lastRetrievedAt;

  return (
    <>
      <div className="sk-detail-head">
        <div className="sk-detail-title">
          <h2>{skill.title}</h2>
          <Badge>{kindLabel(skill.kind)}</Badge>
          <Badge tone={status.tone}>{status.label}</Badge>
        </div>
        <span className="sk-prov">
          {authorText(skill)}
          {' · '}
          <span title={exactDate(skill.createdAt)}>{shortDate(skill.createdAt)}</span>
          {' · '}
          {scopeLabel(skill.scope, lookups, 'long')}
        </span>
      </div>

      <div className="sk-detail-body">
        {skill.kind && skill.kind !== 'procedure' ? (
          <Banner tone="warn" icon={AlertTriangle} title={`This is a ${kindLabel(skill.kind).toLowerCase()} note, not a procedure`}>
            Fix the source ({skill.kind === 'tool_gap' ? 'the tool' : 'the data or mapping'}) rather than approving a workaround into the assistant&apos;s instructions — approved notes are shown to the assistant like any playbook.
          </Banner>
        ) : null}

        {skill.summary && skill.summary !== skill.title ? (
          <Facet label="When to use">
            <p className="sk-summary">{skill.summary}</p>
          </Facet>
        ) : null}

        <Facet label="Playbook">
          <pre className="ak-pre">{skill.body}</pre>
        </Facet>

        <div className="sk-facets">
          <Facet label="Triggers">
            {(t.phrases?.length || t.intents?.length) ? (
              <div className="ak-tags">
                {(t.phrases || []).map((p) => <Tag key={`p:${p}`}>{p}</Tag>)}
                {(t.intents || []).map((i) => <span key={`i:${i}`} className="sk-mono-tags"><Tag>{i}</Tag></span>)}
              </div>
            ) : <span className="sk-none">None</span>}
          </Facet>
          <Facet label="Tools">
            <Tags items={t.tools} mono />
          </Facet>
          {t.workflows?.length ? (
            <Facet label="Workflows">
              <Tags items={t.workflows} mono />
            </Facet>
          ) : null}
          <Facet label="Evidence">
            {prov.threadId ? (
              <span className="sk-evidence">
                <ThreadLink threadId={prov.threadId} ownerId={prov.userId} viewerId={viewerId}>
                  {evidence.join(' · ') || plural(1, 'conversation')}
                </ThreadLink>
              </span>
            ) : (
              <span className="sk-none">{(prov.author || 'model') === 'model' ? 'No conversation recorded' : 'Written by hand'}</span>
            )}
            {detailLoading && prov.reflectionId && !prov.signals ? <span className="sk-muted-sm">Loading reflection…</span> : null}
          </Facet>
        </div>

        {prov.evidence || reflection?.reason ? (
          <Facet label="Why the assistant proposed it">
            {prov.evidence ? <p className="sk-summary">{prov.evidence}</p> : null}
            {reflection?.reason && reflection.reason !== prov.evidence ? (
              <p className="sk-summary sk-muted-p">Reflection: {reflection.reason}</p>
            ) : null}
            {prov.model ? <span className="sk-muted-sm">Model {prov.model}</span> : null}
          </Facet>
        ) : null}

        <Facet label="Usage">
          <span className="sk-stats">
            Added to instructions {plural(stats.injections || 0, 'time')} · found by search {plural(stats.retrievals || 0, 'time')} · {plural(stats.unhelpful || 0, 'unhelpful rating')}
            {lastUsed ? <> · last used <span title={exactDate(lastUsed)}>{relativeTime(lastUsed)}</span></> : null}
            {' · '}v{skill.version || 1}
            {skill.lastEditedBy ? ` · last edited by ${actorText(skill.lastEditedBy)}` : ''}
          </span>
        </Facet>

        {history.length ? (
          <Facet label="History">
            <ol className="sk-history">
              {[...history].reverse().map((h, i) => (
                <li key={`${h.at}-${i}`}>
                  <span className="sk-history-what">{historyText(h)}</span>
                  <span className="sk-muted-sm">
                    {' '}by {actorText(h.actor)} · <span title={exactDate(h.at)}>{relativeTime(h.at) || shortDate(h.at)}</span>
                  </span>
                  {h.note ? <span className="sk-history-note">“{h.note}”</span> : null}
                </li>
              ))}
            </ol>
          </Facet>
        ) : null}

        {statusActions ? (
          <div className="sk-section">
            <label className="ak-section-label" htmlFor={noteId}>Reason</label>
            <textarea
              id={noteId}
              className="ak-textarea sk-note"
              value={note}
              maxLength={1000}
              disabled={busy}
              placeholder="Optional. Saved with your decision in the history; the assistant sees it if it reflects on the same conversation again."
              onChange={(e) => onNote(e.target.value)}
            />
          </div>
        ) : null}

        {actionError ? (
          <Banner tone="bad" icon={AlertTriangle} title="That didn't work">{actionError}</Banner>
        ) : null}
      </div>

      <footer className="ak-panel-foot sk-foot">
        <span className="sk-foot-help">{footerHelp(skill, lookups)}</span>
        <div className="sk-foot-actions">
          {actions.map((a) => (
            <Button
              key={a.key}
              variant={a.variant}
              disabled={busy}
              onClick={() => onAction(a)}
            >
              {busyAction === a.key ? `${a.label}…` : a.label}
            </Button>
          ))}
        </div>
      </footer>
    </>
  );
}
